import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { NetworkMetadata } from '@olv/ui-shared/models/network-metadata.model';

@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  private static readonly GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  private static readonly addressPatterns = new Map<string, RegExp | null>();

  /**
   * Validates a text input based on specified constraints including minimum and maximum length,
   * regular expression pattern matching, and optional error messaging.
   *
   * @param {number} minLength - The minimum allowed length of the input text. Defaults to 0.
   * @param {number} maxLength - The maximum allowed length of the input text. Defaults to 100.
   * @param {string} regexString - The regular expression pattern the input text must match. Defaults to '^[\\p{L}\\p{N}\\s-]*$'.
   * @param {string} [errorMessage] - An optional custom error message to return if the input text does not match the regular expression.
   * @return {ValidatorFn} A validation function that checks if the input text meets the specified constraints. It returns `null` if valid or an object containing the validation error details if invalid.
   */
  static textValidator(
    minLength = 0,
    maxLength = 100,
    regexString = '^[\\p{L}\\p{N}\\s-]*$',
  ): ValidatorFn {
    const regex = new RegExp(regexString, 'u');

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      if (!regex.test(value)) {
        return { invalidText: true };
      }

      if (value.length < minLength) {
        return { minLengthExceeded: { minLength } };
      }

      if (value.length > maxLength) {
        return { maxLengthExceeded: { maxLength } };
      }

      return null;
    };
  }

  static addressValidator(networks: () => NetworkMetadata[], networkName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = (control.value || '').trim();
      if (!value) return null;

      const candidates = networks().filter((network) => !networkName || network.name === networkName);
      if (candidates.some((network) => ValidationService.matchesAddressFormat(network, value))) return null;

      const [network] = candidates;
      return networkName && network ? ValidationService.addressFormatError(network, value) : { addressFormatUnknown: true };
    };
  }

  private static matchesAddressFormat(network: NetworkMetadata, value: string): boolean {
    const { regex, length } = network.addressFormat;
    return value.length === length && !!ValidationService.addressPattern(regex)?.test(value);
  }

  private static addressPattern(pattern: string): RegExp | null {
    if (!ValidationService.addressPatterns.has(pattern)) {
      try {
        ValidationService.addressPatterns.set(pattern, new RegExp(pattern));
      } catch {
        ValidationService.addressPatterns.set(pattern, null);
      }
    }
    return ValidationService.addressPatterns.get(pattern) ?? null;
  }

  private static addressFormatError(network: NetworkMetadata, value: string): ValidationErrors {
    const { regex, length } = network.addressFormat;
    const prefix = /^\^([A-Za-z0-9]+)/.exec(regex)?.[1];
    const error = { network: network.name, length, prefix };
    if (value.length !== length || !value.startsWith(prefix ?? '')) return { addressFormat: error };

    const charClass = /^\^[A-Za-z0-9]*(\[[^\]]+\])\{\d+\}\$$/.exec(regex)?.[1];
    const allowed = charClass ? ValidationService.addressPattern(`^${charClass}$`) : null;
    const invalidChars = allowed ? [...new Set(value.slice(prefix?.length ?? 0))].filter((char) => !allowed.test(char)) : [];
    return { addressFormat: { ...error, invalidChars } };
  }

  static addressByChainValidator(networkOf: (networkType: number) => NetworkMetadata | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val: string = (control?.value || '').trim();
      if (!val) return { required: true };

      const parent = control.parent;
      if (!parent) return null;

      const chain = parent.get('blockchain')?.value;

      const network = chain ? networkOf(chain) : undefined;
      if (!network) {
        return { chainNotSelected: true };
      }

      return ValidationService.matchesAddressFormat(network, val) ? null : ValidationService.addressFormatError(network, val);
    };
  }

  static emailValidator(): ValidatorFn {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null;

      if (!regex.test(value)) {
        return { invalidEmail: `Should be a valid email` };
      }

      return null;
    };
  }

  static fileTypeValidator(allowedTypes: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value as File;

      if (!file) {
        return null;
      }

      const fileName = file.name || '';
      const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();

      const fileType = file.type?.toLowerCase() || '';

      const typeMap: { [key: string]: string[] } = {
        '.jpg': ['image/jpeg', 'image/jpg'],
        '.jpeg': ['image/jpeg', 'image/jpg'],
        '.png': ['image/png'],
        '.pdf': ['application/pdf'],
      };

      const extensionAllowed = allowedTypes.some((type) => type.toLowerCase() === fileExtension);

      const typeAllowed = allowedTypes.some((type) => {
        const allowedTypes = typeMap[type.toLowerCase()] || [];
        return allowedTypes.includes(fileType);
      });

      if (!extensionAllowed && !typeAllowed) {
        return {
          invalidFileType: {
            allowedTypes: allowedTypes.join(', '),
            actualType: fileExtension || fileType,
          },
        };
      }

      return null;
    };
  }

  static fileSizeValidator(maxSizeInMB = 10): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value as File;

      if (!file) {
        return null;
      }

      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

      if (file.size > maxSizeInBytes) {
        return {
          fileSizeExceeded: {
            maxSize: maxSizeInMB,
            actualSize: (file.size / (1024 * 1024)).toFixed(2),
          },
        };
      }

      return null;
    };
  }

  static urlValidator(allowedProtocols: string[] = ['http', 'https']): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = control.value;

      if (!rawValue) {
        return null;
      }

      const value = String(rawValue).trim();
      if (!value) {
        return null;
      }

      try {
        const url = new URL(value);
        const protocol = url.protocol.replace(':', '');
        if (allowedProtocols.length && !allowedProtocols.includes(protocol)) {
          return {
            invalidUrlProtocol: {
              allowedProtocols,
              actualProtocol: protocol,
            },
          };
        }

        return null;
      } catch {
        return { invalidUrl: true };
      }
    };
  }

  static amountValidator(getPrecision: () => number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value == null) return null;

      const precision = getPrecision();
      if (precision == null) return null;

      const amount = Number(value);
      if (isNaN(amount) || amount <= 0) {
        return { invalidAmount: true };
      }

      const decimalLength = value.toString().split('.')[1]?.length ?? 0;

      if (decimalLength > precision) {
        return { invalidPrecision: { precision } };
      }

      return null;
    };
  }

  static amountBalanceValidator(getBalance: () => number | null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value == null || value === '') return null;

      const amount = Number(value);
      if (isNaN(amount) || amount <= 0) {
        return { invalidAmount: true };
      }

      const balance = getBalance();

      if (balance != null && amount > balance) {
        return {
          exceedsBalance: {
            balance,
          },
        };
      }

      return null;
    };
  }

  static isValidGuid(value: string): boolean {
    return ValidationService.GUID_REGEX.test(value);
  }

  static currencyBalanceValidator(getBalance: () => number | null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const currency = control.value;
      const balance = getBalance();

      if (currency == null || currency === '') return null;

      if (balance != null && balance <= 0) {
        return { insufficientCurrencyBalance: true };
      }

      return null;
    };
  }
}
